import type { ComponentProps } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import {
  controlBase,
  controlBorder,
  controlSizes,
  useFieldContext,
  type ControlSize,
} from './field-context';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<ComponentProps<'select'>, 'size'> {
  size?: ControlSize;
  invalid?: boolean;
  /** Convenience: render <option>s from data. Children also work. */
  options?: SelectOption[];
  /** Adds a leading empty option (value ""). */
  placeholder?: string;
}

/** Native <select> (best keyboard + mobile behaviour), styled to match. */
export function Select({
  size = 'md',
  invalid,
  options,
  placeholder,
  className,
  id,
  children,
  ...rest
}: SelectProps) {
  const field = useFieldContext();
  const isInvalid = invalid ?? field?.invalid ?? false;
  return (
    <div className="relative">
      <select
        id={id ?? field?.id}
        aria-invalid={isInvalid || undefined}
        aria-describedby={rest['aria-describedby'] ?? field?.describedBy}
        className={cn(
          controlBase,
          controlSizes[size],
          controlBorder(isInvalid),
          'appearance-none pr-9',
          className,
        )}
        {...rest}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options?.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
        {children}
      </select>
      <ChevronDown
        aria-hidden
        className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-fg-subtle"
      />
    </div>
  );
}
